import React, { useEffect, useState, type FC } from 'react';
import { Space } from '@g2u/ui-kit';
import { Helmet } from 'react-helmet-async';
import { observer } from 'mobx-react-lite';
import { Form } from 'antd';

import { RootStore } from 'store';
import { Actions, Invoice, Method, Receipt, RequestPaymentModal } from '.';
import { useServicePayment } from '../hooks';
import { Service, JsonDataItem } from 'types';

export namespace ServicePayment {
  export type Props = {
    isLocked: boolean;
  };

  export const $: FC<Props> = observer((props) => {
    const { isLocked } = props;
    const { job, setJob } = RootStore;
    const [form] = Form.useForm();
    const {
      jobDetails,
      paymentMethods,
      paymentMethod,
      step,
      setPaymentMethod,
      generateInvoice,
      sendEmail,
      requestPayment,
      processPayment,
      invoices,
      receipts,
      receiptsTotal,
      outstandingAmount,
      hasDebt,
      hasCard,
      isDataLoading,
      isJobUpdating,
      emailOptions,
      smsOptions,
      initialContactValues,
      updateThirdParty,
      incompleteDetailsMessage,
    } = useServicePayment(job as Service);
    const [showRequestPaymentModal, setShowRequestPaymentModal] = useState<boolean>(false);
    const handleFormChange = (field: JsonDataItem) => {
      if (field.paymentMethod) {
        setPaymentMethod(field.paymentMethod);
      }
    };

    useEffect(() => {
      jobDetails && setJob(jobDetails);
    }, [jobDetails]);

    return (
      <>
        <Helmet>
          <title>Service Payment</title>
        </Helmet>
        <Form
          form={form}
          name="service-payment-form"
          layout="vertical"
          colon={false}
          preserve={false}
          disabled={isLocked}
          onValuesChange={handleFormChange}
        >
          <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraLarge}>
            <Method.$
              job={job as Service}
              hasDebt={hasDebt}
              step={step}
              paymentMethod={paymentMethod}
              paymentMethods={paymentMethods}
              hasCard={hasCard}
              isLoading={isDataLoading}
              updateThirdParty={updateThirdParty}
              isLocked={isLocked}
            />
            <Invoice.$
              job={job as Service}
              step={step}
              hasDebt={hasDebt}
              invoices={invoices}
              paymentMethod={paymentMethod}
              paymentMethods={paymentMethods}
              isLoading={isDataLoading}
              onRequestPayment={() => setShowRequestPaymentModal(true)}
              onProcessPayment={processPayment}
              incompleteDetailsMessage={incompleteDetailsMessage}
            />
            <Receipt.$
              receipts={receipts}
              receiptsTotal={receiptsTotal}
              outstandingAmount={outstandingAmount}
              hasDebt={hasDebt}
              paymentMethods={paymentMethods}
              isLoading={isDataLoading}
            />
            <Actions.$
              job={job as Service}
              step={step}
              hasCard={hasCard}
              paymentMethod={paymentMethod}
              onSendEmail={sendEmail}
              onGenerateInvoice={generateInvoice}
              onRequestPayment={() => setShowRequestPaymentModal(true)}
              onProcessPayment={processPayment}
              isLocked={isLocked || isDataLoading}
              isLoading={isJobUpdating}
            />
            <RequestPaymentModal.$
              initialValues={initialContactValues}
              emailOptions={emailOptions}
              smsOptions={smsOptions}
              isOpen={showRequestPaymentModal}
              onCancel={() => {
                setShowRequestPaymentModal(false);
              }}
              onRequest={(contactDetails: JsonDataItem) => {
                requestPayment(contactDetails);
                setShowRequestPaymentModal(false);
              }}
            />
          </Space.$>
        </Form>
      </>
    );
  });
}
